import { uuid4 } from "@sentry/utils";
import axios from "axios";
import Uploader from "./uploader";
import * as htmlToImage from "html-to-image";
import { IMAGE_UPLOAD_ERROR } from "../../../constants/general";

export default class fabricApi {
  constructor(authToken, orgID, host, uploadHost) {
    this.token = authToken;
    this.orgID = orgID;
    this.host = host;
    this.uploadHost = uploadHost;
  }

  auth = () => `bearer_token=${this.token}&orgId=${this.orgID}`;

  /**
   * Check user agent for iphone, ipad, or ipod string
   * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgent
   */
  isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);

  /**
   * Check user agent for safari string that is not preceeded by
   * Chrome or Android string
   * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgent
   */
  isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  async createScreenshot(elem, canvas) {
    /**
     * Safari & iOS do not work properly with the htmlToImage library.
     * It can use the native html canvas methods but can not handle large
     * canvases. So for these platforms we shrink the canvas, and then
     * convert it to a blob. It runs twice to make sure the blob is fully
     * constructed.
     */
    let blob = null;
    if (this.isIOS() || this.isSafari()) {
      elem.style.transform = 'scale(0.25, 0.25)';
      blob = await new Promise((resolve) => {
        canvas.toBlob(() => {
          canvas.toBlob((data) => {
            resolve(data);
          })
        });
      });
    } else {
      blob = await new Promise((resolve) => {
        htmlToImage.toBlob(elem).then(function (data) {
          resolve(data);
        });
      });
    }

    // Return image file for upload
    return new File([blob], "thumbnail.png", { type: "image/png" });
  }

  async getSnapShotUrl() {
    // Get player and canvas elements
    const elem = document.getElementById("fabricPlayer");
    const canvas = document.getElementById("canvas");

    // Create image for upload
    const image = await this.createScreenshot(elem, canvas);

    let formdata = new FormData();
    formdata.append("image", image);

    return this.processRequest(formdata);
  }

  async getMultipleSnapShotUrl(node) {
    const back = document.getElementById("back");
    const front = document.getElementById("front");

    // Bring the current div to the front for screenshot
    if (node === "front") {
      back.style.zIndex = "0";
      front.style.zIndex = "100";
    } else {
      front.style.zIndex = "0";
      back.style.zIndex = "100";
    }

    // Get player and canvas elements
    const elem = document.getElementById("fabricPlayer");
    const backCanvas = document.getElementById("canvasBack");
    const frontCanvas = document.getElementById("canvas");
    const currentCanvas = node === "front" ? frontCanvas : backCanvas;

    // Create image for upload
    const image = await this.createScreenshot(elem, currentCanvas);

    let formdata = new FormData();
    formdata.append("image", image);

    return this.processRequest(formdata);
  }

  async handleDirectUpload(file) {
    const uploader = new Uploader(file)
    let response = await uploader.uploadAsset();
    let formdata = new FormData();
    formdata.append("image", response);
    return await this.processRequest(formdata);
  };

  async uploadFile(filedata) {
    let formdata = new FormData();
    formdata.append("image", filedata);
    return this.processRequest(formdata);
  }

  async processRequest(formdata) {
    formdata.append("shopify_customer_id", __st.cid || null);
    var requestOptionsUpload = {
      method: "POST",
      body: formdata,
      redirect: "follow",
    };

    var uploadPath = `https://${this.uploadHost}/api/public/customer_images?`;

    let response = await fetch(uploadPath + this.auth(), requestOptionsUpload)
      .then((response) => response.json())
      .then((result) => {
        return result;
      })
      .catch((error) => {
        console.log("Error while uploading", error)
        return  IMAGE_UPLOAD_ERROR;
    });

    return response;
  }

  async fetchImage(imageId) {
    var requestOptionsFetch = {
      method: "GET",
      redirect: "follow",
    };

    var fetchPath =
      `https://${this.uploadHost}/api/public/customer_images/` + imageId + "?";

    let response = await fetch(fetchPath + this.auth(), requestOptionsFetch)
      .then((response) => response.json())
      .then((result) => {
        return result;
      })
      .catch((error) => console.log("error", error));

    return response.image_url;
  }

  async fetchArtworkImage(imageId) {
    var requestOptionsFetch = {
      method: "GET",
      redirect: "follow",
    };

    var fetchPath =
      `https://${this.uploadHost}/api/public/artwork_images/` + imageId + "?";

    let response = await fetch(fetchPath + this.auth(), requestOptionsFetch)
      .then((response) => response.json())
      .then((result) => {
        return result;
      })
      .catch((error) => console.log("error", error));

    return response.image_url;
  }

  async convertToFile(image) {
    const url = image.split("?")[0];
    const fileName = url.substring(url.lastIndexOf("/") + 1, url.length);
    const fileType = fileName.substring(
      fileName.lastIndexOf(".") + 1,
      fileName.length
    );
    const contentType = "image/" + fileType;

    return fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        return new File([blob], fileName, { contentType });
      })
      .catch((error) => console.log("error", error));
  }

  async convertArtworkToFile(url) {
    const fileName = uuid4();
    const fileType = "jpg";
    const contentType = "image/" + fileType;

    console.log(url);

    return fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        return new File([blob], fileName, { contentType });
      })
      .catch((error) => console.log("error", error));
  }

  /**
   * Fetch mockup from mediamodifier
   *
   * @param {Object}  mockupId  Mediamodifier mockup ID
   *
   * @return {Object}
   */
  async fetchProductMockup(mockupId) {
    const apiPath = `https://${this.uploadHost}/api/public/media_modifier/single_mockup?id=` + mockupId;

    return axios.get(apiPath).then(({data}) => {
      return data.response;
    });
  }

  /**
   * Render product mockup call for mediamodifier
   *
   * @param {Object}  payload  API request payload, mockup id and layer info
   *
   * @return {Object}
   */
  async fetchProductMockupRender(payload) {
    const apiPath = `https://${this.uploadHost}/api/public/media_modifier/render_mockup?`;

    return axios.post(
      apiPath,
      payload,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
      }
    ).then(({data}) => {
      return data.response;
    });
  }

  /**
   * Check if purl code is valid
   * @param {String} purl Purl code
   * @returns {Boolean}
   */
  async checkPurlCode(purl) {
    const apiPath = `https://${this.uploadHost}/api/public/validate/${purl}`;

    return await axios
      .get(apiPath)
      .then((response) => response.data.status || false)
      .catch((error) => false);
  }
}
